import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { SitesService } from 'src/app/core/services/api/sites.service';
import { SiteResponse } from 'src/app/core/models/sites-management/site-response';
import { Subject, Subscription, throwError, timer } from 'rxjs';
import { exhaustMap, filter, retry, switchMap, take, takeUntil } from 'rxjs/operators';
import { SiteSetupStage } from '../site-setup-illustration/site-setup-illustration.component';

const Status = SiteResponse.BuildingStatus;

@Component({
  selector: 'app-site-building-status-modal',
  templateUrl: './site-building-status-modal.component.html',
  styleUrls: ['./site-building-status-modal.component.scss']
})
export class SiteBuildingStatusModalComponent implements OnInit, OnDestroy {
  @Input() siteId: string;
  @Input() siteName = '';
  /** The caller opens this before POST completes, then supplies the created site's ID. */
  @Input() deferStart = false;

  currentStatus = Status.PREPARING_EXAMPLE_DATA;
  siteInfo: SiteResponse;
  waitingForAuth = true;
  trackingError = false;
  takingLonger = false;

  readonly steps = [
    { status: Status.PREPARING_EXAMPLE_DATA, title: 'site_setup_catalog_title', detail: 'site_setup_catalog_detail', icon: 'bi-box-seam', stage: 'catalog' as SiteSetupStage },
    { status: Status.PREPARING_STORE, title: 'site_setup_store_title', detail: 'site_setup_store_detail', icon: 'bi-layout-text-window', stage: 'store' as SiteSetupStage },
    { status: Status.CONFIGURING_SUB_DOMAIN, title: 'site_setup_domain_title', detail: 'site_setup_domain_detail', icon: 'bi-globe2', stage: 'domain' as SiteSetupStage }
  ];
  private readonly destroy$ = new Subject<void>();
  private tracking?: Subscription;
  private finishing = false;
  private destroyed = false;

  constructor(
    public activeModal: NgbActiveModal,
    private sitesService: SitesService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    timer(60000).pipe(takeUntil(this.destroy$)).subscribe(() => this.takingLonger = true);
    if (this.deferStart) { this.waitingForAuth = false; return; }
    if (this.siteId) this.beginTracking(this.siteId, this.siteName);
    else this.waitForAccount();
  }

  get isComplete(): boolean { return this.currentStatus === Status.INITIALIZED; }
  get isStarting(): boolean { return this.waitingForAuth || !this.siteId; }
  get currentStepIndex(): number {
    if (this.isStarting) return -1;
    return this.isComplete ? this.steps.length : this.steps.findIndex(step => step.status === this.currentStatus);
  }
  get illustrationStage(): SiteSetupStage {
    if (this.isComplete) return 'complete';
    return this.steps[this.currentStepIndex]?.stage || 'identity';
  }
  get currentTitleKey(): string {
    if (this.trackingError) return 'site_setup_tracking_error_title';
    if (this.isComplete) return 'site_setup_ready_title';
    if (this.isStarting) return 'site_setup_starting_title';
    return ['site_setup_catalog_activity', 'site_setup_store_activity', 'site_setup_domain_activity'][this.currentStepIndex] || 'site_setup_starting_title';
  }
  get currentDetailKey(): string {
    if (this.trackingError) return 'site_setup_tracking_error_detail';
    if (this.isComplete) return 'site_setup_ready_detail';
    if (this.isStarting) return 'site_setup_starting_detail';
    return this.steps[this.currentStepIndex]?.detail || 'site_setup_starting_detail';
  }
  get shopAddress(): string { return (this.siteInfo?.sub_domain || '').replace(/^https?:\/\//, '').replace(/\/$/, ''); }

  beginTracking(siteId: string, siteName: string): void {
    if (this.finishing || this.destroyed) return;
    this.siteId = siteId;
    this.siteName = siteName;
    this.waitingForAuth = false;
    this.trackingError = false;
    this.tracking?.unsubscribe();
    // A slow request must finish: switchMap at 300 ms used to cancel valid responses.
    this.tracking = timer(0, 1000).pipe(
      exhaustMap(() => this.sitesService.getMySite(this.siteId)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (site: SiteResponse) => this.receiveSite(site),
      error: () => this.trackingError = true
    });
  }

  retryTracking(): void {
    if (this.finishing || this.destroyed) return;
    this.trackingError = false;
    if (this.siteId) this.beginTracking(this.siteId, this.siteName);
    else this.waitForAccount();
  }

  private waitForAccount(): void {
    this.waitingForAuth = true;
    this.tracking?.unsubscribe();
    this.tracking = timer(0, 500).pipe(
      filter(() => !!localStorage.getItem('access_token')),
      take(1),
      switchMap(() => this.sitesService.getMySites()),
      // Registration can still be completing authentication when the dialog opens.
      retry({ delay: error => error.status === 401 || error.status === 403 ? timer(1000) : throwError(() => error) }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (sites: SiteResponse[]) => {
        const site = Array.isArray(sites) ? sites[0] : null;
        if (site?.id) this.beginTracking(site.id, site.site_name);
        else this.trackingError = true;
      },
      error: () => this.trackingError = true
    });
  }

  private receiveSite(site: SiteResponse): void {
    if (this.finishing) return;
    // The enum also exposes the intermediate event after sample data is created.
    const status = site.building_status === Status.EXAMPLE_DATA_CREATED ? Status.PREPARING_STORE : site.building_status;
    const index = status === Status.INITIALIZED ? this.steps.length : this.steps.findIndex(step => step.status === status);
    if (index < 0 || index < this.currentStepIndex) return;
    this.siteInfo = site;
    this.siteName = site.site_name || this.siteName;
    this.currentStatus = status;
    if (!this.isComplete) return;

    this.finishing = true;
    this.tracking?.unsubscribe();
    timer(1800).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.activeModal.close({ success: true, site: this.siteInfo, message: this.translate.instant('sidebar.site_created_success') });
    });
  }

  closeTrackingError(): void {
    if (this.trackingError) this.activeModal.dismiss('tracking-unavailable');
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.tracking?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
